import React, { useState } from 'react';
import { Button, Form, message, Typography } from 'antd';
import { FiMail, FiInfo, FiChevronUp } from 'react-icons/fi';
import { useGenericMutation } from '../../../../queries/use-generic-mutation';
import { MSG_CHANGE_USER_EMAIL } from '../../../../utils/message';
import CommonCard from '../../../global/card';
import CommonForm from '../../../global/form';
import SOURCES from '../../../../utils/constants';
import { useGenericQuery } from '../../../../queries/use-generic-query';
import { cn } from '../../../../utils/cn';
import CommonDrawer from '../../../global/drawer';
import EnhancedChangeUserEmailDetail from './enhancedChangeUserEmailDetail';


const EnhancedChangeUserEmail = () => {
    const [form] = Form.useForm();
    const [showDetail, setShowDetail] = useState(false);
    const currentEmail = Form.useWatch('currentEmail', form);
    const { Text } = Typography;

    // Mutation for changing email
    const changeEmailMutation = useGenericMutation({
        endpoint: 'admin/change/user/email',
        onSuccess: () => {
            message.success(MSG_CHANGE_USER_EMAIL.CHANGE_EMAIL);
            form.resetFields();
            setShowDetail(false);
        },
        onError: (err) => {
            message.error(err?.response?.data?.message || "Failed to change email");
        }
    });

    // Query for fetching user details
    const { data: userDetail, isLoading: isUserLoading, refetch: fetchUserDetail } = useGenericQuery({
        endpoint: `admin/user-detail`,
        queryParams: { email: currentEmail },
        queryKey: ["admin", "user-detail", currentEmail],
        enabled: false, // Only trigger manually or on button click
    });

    const handleFetchDetail = async () => {
        if (!currentEmail) {
            message.warning("Please enter a current email address first.");
            return;
        }
        try {
            await form.validateFields(['currentEmail']);
            setShowDetail(true);
            fetchUserDetail();
        } catch (err) {
            // Validation failed
        }
    };

    const handleFinish = async (values) => {
        await changeEmailMutation.mutateAsync({
            currentEmail: values.currentEmail,
            newEmail: values.newEmail
        });
    };

    const formData = [
        {
            label: "Current Email Address",
            name: "currentEmail",
            type: "text",
            inputType: SOURCES.FORMINPUTTYPES.INPUT,
            required: true,
            placeholder: "Enter existing email address",
            span: 12,
            message: "Current email is required",
        },
        {
            label: "New Email Address",
            name: "newEmail",
            type: "text",
            inputType: SOURCES.FORMINPUTTYPES.INPUT,
            required: true,
            placeholder: "Enter new email address",
            span: 12,
            message: "New email is required",
        }
    ];

    return (
        <CommonCard
            titleClassName="text-2xl font-bold text-gray-800"
            className="shadow-xl border-none overflow-hidden"
        >

            <CommonForm
                form={form}
                data={formData}
                onFinish={handleFinish}
            />

            {/* Action Buttons */}
            <div className="flex flex-wrap items-center gap-4 mt-3 pt-6 border-t border-gray-100">
                <Button
                    onClick={handleFetchDetail}
                    className={cn(
                        "h-10 px-6  font-semibold transition-all flex items-center gap-2", "bg-primary/5 text-primary border-primary/20 hover:!bg-primary/10 hover:!border-primary/20"
                    )}
                >
                    <FiInfo />
                    Show User Detail
                </Button>

                <Button
                    type="primary"
                    onClick={() => form.submit()}
                    loading={changeEmailMutation.isPending}
                    className="!bg-primary text-white h-10 min-w-[130px]"
                >
                    <FiMail className="mr-2" />
                    Change Email
                </Button>
            </div>

            {/* User Details Drawer */}
            {showDetail && (
                <CommonDrawer
                    open={showDetail}
                    setOpen={setShowDetail}
                    title="User Details"
                    width={"600px"}
                >
                    {isUserLoading ? (
                        <div className="flex flex-col items-center justify-center p-20 !min-h-[600px]">
                            <div className="relative size-12 mb-4">
                                <div className="absolute inset-0 rounded-full border-4 border-primary/10" />
                                <div className="absolute inset-0 rounded-full border-4 border-transparent border-t-primary animate-spin" />
                            </div>
                            <Text type='secondary' className="animate-pulse">Loading user details...</Text>
                        </div>
                    ) : (
                        <EnhancedChangeUserEmailDetail
                            userDetail={userDetail}
                            setShowDetail={setShowDetail}
                        />
                    )}
                </CommonDrawer>
            )}
        </CommonCard>
    );
};

export default EnhancedChangeUserEmail;
